import { decode as decodeBolt11 } from "@gandlaf21/bolt11-decode";

export interface RouteHintHop {
  pubkey: string;
  short_channel_id: string;
  fee_base_msat: number;
  fee_proportional_millionths: number;
  cltv_expiry_delta: number;
}

/** All the fields we extract from a decoded invoice, in plain JSON format */
export interface DecodedInvoiceSummary {
  paymentRequest: string;
  destination: string | null;
  destinationAlias: string | null;
  amountInSatoshis: number | null;
  description: string | null;
  /** Hex sha256 of a description kept elsewhere (LNURL / Lightning Address invoices) */
  descriptionHash: string | null;
  paymentHash: string | null;
  paymentSecret: string | null;
  timestamp: number | null;
  expiry: number | null;
  network: string | null;
  networkLetters: string | null;
  minFinalCltvExpiry: number | null;
  routeHints: RouteHintHop[][];
  signature: string | null;
  fallbackAddress: string | null;
  /** Map of pubkey -> alias for all pubkeys in the invoice */
  pubkeyAliases: Record<string, string | null>;
  /** Is the destination in Rizful's copy of the public Lightning graph? null = unknown */
  destinationInGraph: boolean | null;
  /** Does the destination advertise keysend (from the same graph data)? */
  destinationKeysend: boolean | null;
}

/** Pubkey with what our copy of the public Lightning graph knows about it */
export interface PubkeyWithAlias {
  pubkey: string;
  alias: string | null;
  in_graph?: boolean;
  keysend?: boolean;
}

/** Response from server API */
export interface ServerDecodeResponse {
  destination: string | null;
  destination_alias: string | null;
  destination_in_graph?: boolean | null;
  destination_keysend?: boolean | null;
  route_hints: RouteHintHop[][];
  all_pubkeys: PubkeyWithAlias[];
  error?: string;
}

/**
 * Decode using lightweight @gandlaf21/bolt11-decode (client-side, no polyfills needed).
 * This gets basic fields but NOT the destination pubkey or route hints.
 */
export function summariseDecodedInvoiceClient(
  bolt11String: string
): Omit<
  DecodedInvoiceSummary,
  | "destination"
  | "destinationAlias"
  | "routeHints"
  | "pubkeyAliases"
  | "destinationInGraph"
  | "destinationKeysend"
> {
  const decoded = decodeBolt11(bolt11String);
  const sections = decoded.sections;

  // Helper to find a section by name
  const findSection = (name: string) =>
    sections.find((s: any) => s.name === name);

  // Helper to convert Buffer/Uint8Array to hex string
  const toHexString = (value: any): string | null => {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (
      value instanceof Uint8Array ||
      (value.type === "Buffer" && Array.isArray(value.data))
    ) {
      const bytes =
        value instanceof Uint8Array ? value : new Uint8Array(value.data);
      return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    }
    return String(value);
  };

  // Amount - comes in millisatoshis
  let amountInSatoshis: number | null = null;
  const amountSection = findSection("amount");
  if (amountSection?.value) {
    // Value is in millisatoshis
    amountInSatoshis = Math.floor(parseInt(amountSection.value, 10) / 1000);
  }

  // Description
  const descSection = findSection("description");
  const description = descSection?.value ?? null;

  // Description hash ('h' tag): LNURL-pay / Lightning Address invoices commit to the
  // metadata instead of carrying the text
  const descriptionHash = toHexString(findSection("description_hash")?.value);

  // Payment hash - convert Buffer to hex string
  const hashSection = findSection("payment_hash");
  const paymentHash = toHexString(hashSection?.value);

  // Payment secret - convert Buffer to hex string
  const secretSection = findSection("payment_secret");
  const paymentSecret = toHexString(secretSection?.value);

  // Timestamp
  const timestampSection = findSection("timestamp");
  const timestamp = timestampSection?.value ?? null;

  // Expiry
  const expirySection = findSection("expiry");
  const expiry = expirySection?.value ?? 3600;

  // Network from prefix (lnbc = mainnet, lntb = testnet, etc.)
  const prefixSection = findSection("coin_network");
  const networkLetters = prefixSection?.value?.bech32 ?? null;
  let network: string | null = null;
  if (networkLetters) {
    switch (networkLetters) {
      case "bc":
        network = "Bitcoin Mainnet";
        break;
      case "tb":
        network = "Bitcoin Testnet";
        break;
      case "bcrt":
        network = "Bitcoin Regtest";
        break;
      case "tbs":
        network = "Bitcoin Signet";
        break;
      case "sb":
        network = "Bitcoin Simnet";
        break;
      default:
        network = networkLetters;
    }
  }

  // Min final CLTV expiry
  const cltvSection = findSection("min_final_cltv_expiry");
  const minFinalCltvExpiry = cltvSection?.value ?? null;

  // Signature - convert Buffer to hex string
  const sigSection = findSection("signature");
  const signature = toHexString(sigSection?.value);

  // Fallback address
  const fallbackSection = findSection("fallback_address");
  const fallbackAddress = fallbackSection?.value ?? null;

  return {
    paymentRequest: bolt11String,
    amountInSatoshis,
    description,
    descriptionHash,
    paymentHash,
    paymentSecret,
    timestamp,
    expiry,
    network,
    networkLetters,
    minFinalCltvExpiry,
    signature,
    fallbackAddress,
  };
}

/** A failed POST /api/decode_invoice; status 429 means the visitor hit the rate limit. */
export class DecodeRequestError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

/**
 * Fetch destination and route hints from server API.
 * The server uses Ruby's secp256k1 to recover the destination pubkey from signature.
 */
export async function fetchServerDecode(
  bolt11String: string
): Promise<ServerDecodeResponse> {
  const response = await fetch("/api/decode_invoice", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token":
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content") ?? "",
    },
    body: JSON.stringify({ invoice: bolt11String }),
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    const message =
      response.status === 429
        ? "Too many decodes from your connection. Please wait a minute and try again."
        : data.error || "Server decode failed";
    throw new DecodeRequestError(message, response.status);
  }

  return response.json();
}

/**
 * Full decode: client-side for basic fields + server for destination/route hints.
 */
export async function summariseDecodedInvoice(
  bolt11String: string
): Promise<DecodedInvoiceSummary> {
  // Get basic fields from lightweight client decoder
  const clientResult = summariseDecodedInvoiceClient(bolt11String);

  // Get destination and route hints from server
  const serverResult = await fetchServerDecode(bolt11String);

  // Build pubkey -> alias map from server response
  const pubkeyAliases: Record<string, string | null> = {};
  for (const item of serverResult.all_pubkeys) {
    pubkeyAliases[item.pubkey] = item.alias;
  }

  return {
    ...clientResult,
    destination: serverResult.destination,
    destinationAlias: serverResult.destination_alias,
    routeHints: serverResult.route_hints,
    pubkeyAliases,
    destinationInGraph: serverResult.destination_in_graph ?? null,
    destinationKeysend: serverResult.destination_keysend ?? null,
  };
}
